/**
 * Commesse e progetti (docs/adr/ADR-022, decisions of 16/10/2026). A commessa
 * is the work with one client: it gathers the offers accepted over time. A
 * progetto is internal work, without client and with costs only. Same model
 * for both: code, states, the figures of the work (PM, Account, Commerciale).
 *
 * Who changes a commessa is decided by the permissions of the module
 * «Commesse e progetti», not by the figures: the figures see it (the
 * commerciale only when the box «vede la commessa» is on).
 */
import { z } from 'zod';
import { idSchema, isoDateSchema, optionalText, requiredText } from './validation.js';
export const JOB_KINDS = ['commessa', 'progetto'];
export const JOB_KIND_LABELS = {
    commessa: 'Commessa',
    progetto: 'Progetto',
};
/** Prefix of the code: COM-2026-0001, PRJ-2026-0001. */
export const JOB_CODE_PREFIXES = { commessa: 'COM', progetto: 'PRJ' };
/** Code of a commessa or a progetto: by year, without gaps. */
export function jobCode(kind, year, progressive) {
    return `${JOB_CODE_PREFIXES[kind]}-${year}-${String(progressive).padStart(4, '0')}`;
}
/** Code of a sottocommessa (17/10/2026): the code of its madre and -01, -02… */
export function childJobCode(parentCode, progressive) {
    return `${parentCode}-${String(progressive).padStart(2, '0')}`;
}
export const JOB_STATES = [
    'in_preparazione',
    'in_corso',
    'sospesa',
    'chiusa',
    'annullata',
];
export const JOB_STATE_LABELS = {
    in_preparazione: 'In preparazione',
    in_corso: 'In corso',
    sospesa: 'Sospesa',
    chiusa: 'Chiusa',
    annullata: 'Annullata',
};
/** The states still open: a commessa in them takes new offers. */
export const OPEN_JOB_STATES = ['in_preparazione', 'in_corso', 'sospesa'];
export function isOpenJob(state) {
    return OPEN_JOB_STATES.includes(state);
}
const NEXT_STATES = {
    in_preparazione: ['in_corso', 'annullata'],
    in_corso: ['sospesa', 'chiusa'],
    sospesa: ['in_corso', 'chiusa'],
    // A closed commessa opens again; an annulled one stays so.
    chiusa: ['in_corso'],
    annullata: [],
};
export function nextJobStates(state) {
    return NEXT_STATES[state];
}
/** The figures of a commessa (16/10/2026): one each for now, more allowed. */
export const JOB_FIGURES = ['pm', 'account', 'commerciale'];
export const JOB_FIGURE_LABELS = {
    pm: 'PM',
    account: 'Account',
    commerciale: 'Commerciale',
};
export const JOB_FIGURE_DESCRIPTIONS = {
    pm: 'Project Manager: vede tutta la commessa.',
    account: 'Gestisce il cliente e lo accompagna fino al termine della commessa: vede tutta la commessa.',
    commerciale: 'Ha venduto la commessa: la vede solo con «vede la commessa».',
};
/** Whether a figure sees the commessa: PM and Account always, the commerciale when allowed. */
export function figureSees(figure, canSee) {
    return figure !== 'commerciale' || canSee;
}
/** The figures a progetto has (26/10/2026): only the PM, there is no client. */
export const PROJECT_FIGURES = ['pm'];
/** What a commessa or a progetto lacks to start: an empty list when it may go «in corso». */
export function startProblems(job) {
    const problems = [];
    const has = (figure) => job.people.some((person) => person.figure === figure);
    if (!has('pm'))
        problems.push('manca il PM');
    // A progetto has no client, so no Account (26/10/2026).
    if (job.kind !== 'progetto' && !has('account'))
        problems.push('manca l’Account');
    if (!job.startOn || !job.endOn)
        problems.push('mancano le date previste');
    return problems;
}
/** Why a commessa cannot move to a state; null when it can. */
export function jobStateProblem(job, next) {
    if (!nextJobStates(job.state).includes(next)) {
        return `Da «${JOB_STATE_LABELS[job.state]}» non si passa a «${JOB_STATE_LABELS[next]}»`;
    }
    // The madre closes only after all its figlie (17/10/2026).
    const openChildren = (job.childStates ?? []).filter(isOpenJob).length;
    if ((next === 'chiusa' || next === 'annullata') && openChildren > 0) {
        return `Prima vanno chiuse o annullate le sottocommesse: ${openChildren === 1 ? 'ne resta aperta 1' : `ne restano aperte ${openChildren}`}`;
    }
    if (next === 'in_corso') {
        const problems = startProblems(job);
        if (problems.length)
            return `Non può andare in corso: ${problems.join(', ')}`;
    }
    if (next === 'annullata' && job.offerCount > 0) {
        return 'Si annulla solo senza offerte: togli prima le offerte collegate';
    }
    return null;
}
/* --- Requests ------------------------------------------------------------------ */
const jobFields = {
    name: requiredText(200),
    description: optionalText(2000),
    startOn: isoDateSchema.nullable().default(null),
    endOn: isoDateSchema.nullable().default(null),
    notes: optionalText(2000),
    /** «Si fattura a» (17/10/2026): a subject with the role Cliente; null: the client itself. */
    billingClientId: idSchema.nullable().default(null),
    /** Progetti only (26/10/2026): the code of the table «Aree dei progetti». */
    areaCode: optionalText(30),
    /** Progetti only: the objective to reach, measured by its indicators. */
    objective: optionalText(2000),
};
function datesInOrder(body, context) {
    if (body.startOn && body.endOn && body.endOn < body.startOn) {
        context.addIssue({
            code: 'custom',
            path: ['endOn'],
            message: 'La fine viene dopo l’inizio',
        });
    }
}
/** A new commessa (with its client) or progetto (without). */
export const newJobSchema = z
    .object({ kind: z.enum(JOB_KINDS), clientId: idSchema.nullable(), ...jobFields })
    .superRefine((body, context) => {
    if (body.kind === 'commessa' && !body.clientId) {
        context.addIssue({ code: 'custom', path: ['clientId'], message: 'Scegli il cliente' });
    }
    if (body.kind === 'progetto' && body.clientId) {
        context.addIssue({
            code: 'custom',
            path: ['clientId'],
            message: 'Un progetto non ha cliente',
        });
    }
    if (body.kind === 'commessa' && (body.areaCode || body.objective)) {
        context.addIssue({
            code: 'custom',
            path: ['areaCode'],
            message: 'Area e obiettivo sono dei progetti',
        });
    }
    datesInOrder(body, context);
});
/** The change of a commessa: kind and client stay. */
export const jobSchema = z.object(jobFields).superRefine(datesInOrder);
export const jobPeopleSchema = z
    .object({
    people: z
        .array(z.object({
        userId: idSchema,
        figure: z.enum(JOB_FIGURES),
        /** For the commerciale: whether they see the commessa. Ignored for the others. */
        canSee: z.boolean().default(false),
    }))
        .max(30),
})
    .superRefine((body, context) => {
    const seen = new Set();
    body.people.forEach((person, index) => {
        const key = `${person.figure}:${person.userId}`;
        if (seen.has(key)) {
            context.addIssue({
                code: 'custom',
                path: ['people', index, 'userId'],
                message: 'La stessa persona è già in questa figura',
            });
        }
        seen.add(key);
    });
});
export const jobStateSchema = z.object({ state: z.enum(JOB_STATES) });
/** Linking an accepted offer: to a commessa already open, or to a new one with its name. */
export const offerLinkSchema = z
    .object({ jobId: idSchema.nullable(), newJobName: optionalText(200) })
    .refine((body) => (body.jobId === null) !== (body.newJobName === null), {
    message: 'Scegli una commessa oppure il nome della nuova',
    path: ['jobId'],
});
export function jobLabel(job) {
    return `${job.code} · ${job.name}`;
}
//# sourceMappingURL=jobs.js.map